<template>
    <div class="product-explorer">
      <div v-if="debug" class="my-3">
        <h3>Query</h3>
        <code class="code">{{ JSON.stringify(query) }}</code>
        <h3>Filters</h3>
        <code class="code">{{ JSON.stringify(filters) }}</code>
      </div>
      <section v-if="currentSearchQuery == null" class="">
        <h3>No search query..</h3>
      </section>
      <section v-if="currentSearchQuery != null" class="">
        <ProductCardPlaceholder v-if="isSearching" :rows="5" loading type="media-square" align="left"/>
        <div class="container" v-if="!isSearching">
          <!-- Start: Filters Panel -->
          <transition name="fade">
            <ProductFilters v-if="showFilters" :values="searchFilters" :facets="facets" @closed="showFilters = false" @apply="applyFilters($event)" />
          </transition>
          <!-- End: Filters Panel -->
          <ProductNoProductsAvailable
              v-if="noSearchResultsAvailable"
              @retry="retrySearch()"
              @refine="refineFilters()"
              :query="currentSearchQuery"
              :filters="searchFilters" :filtersModified="filtersAreModified"
              :stats="{ 'maxLtv': 0.95 }" :disableModals="disableSearchModal"/>
          <div v-if="searchResultsAvailable">
            <div class="row mb-3">
              <transition name="">
                <div v-if="false && !showFilters && totalResults > 50 && !usingDefaultQuery" class="col">
                  <!-- CTA Section -->
                  <div class="ww-lg-85 mx-lg-auto">
                    <div class="card bg-secondary text-white overflow-hidden p-4">
                      <div class="row justify-content-md-start align-items-md-center text-center text-md-left">
                        <div class="col-md-7 offset-md-3 mb-3 mb-md-0">
                          <h3 class="text-white mb-1">Confused by all the options?</h3>
                          <p class="text-white mb-0">Filter your results to find the right deals for you.</p>
                        </div>
                        <div class="col-md-2 text-md-right">
                          <button class="btn btn-light" @click="showFilters = true"><i class="fas fa-sliders-h"></i> Show Filters</button>
                        </div>
                      </div>

                      <!-- SVG Component -->
                      <figure class="w-25 d-none d-md-block content-centered-y ml-n4">
                        <img class="img-fluid" src="/assets/svg/illustrations/communication.svg" alt="Image Description">
                      </figure>
                      <!-- End SVG Component -->
                    </div>
                  </div>
                  <!-- End CTA Section -->
                </div>
              </transition>
            </div>
            <div v-if="showBestBuys" class="row bg-soft-primary pt-4 mb-4">
              <div class="col-12">
                <BestBuyCard :data="bestBuys" 
                             :using-default="isExample" 
                             :search-query="currentSearchQuery" 
                             @closed="showBestBuys = false"></BestBuyCard>
              </div>
            </div>
            <div v-if="!onlyBestBuys">
              <!-- Start: Search Results Header -->
              <div class="d-flex mb-1">
                <div class="align-content-center text-left ppt-2" style="flex: 1 0 auto; flex-basis: auto;">
                  <ul class="list-inline">
                    <li class="list-inline-item"><span class="" style="font-size: large;">Showing <strong>{{ products.length | prettyNumber }}</strong> of <strong>{{ totalResults | prettyNumber }}</strong> products</span></li>
                    <li v-if="!hideFilters" class="list-inline-item"><button class="btn btn-sm btn-outline-secondary" @click="showFilters = !showFilters" title="Show Filters"><i class="fas fa-sliders-h"></i></button></li>
                    <li class="list-inline-item"><button class="btn btn-sm " :class="[{ 'btn-outline-primary': !showBestBuys}, { 'btn-outline-secondary': showBestBuys }]" @click="showBestBuys = true" title="Show Best Buys"><i class="fas fa-trophy"></i></button></li>
                  </ul>
                </div>
                <div class="align-content-center">
                  <SortByDropdown class="" :value="sortByValue" @selected="sortBySelected($event)"></SortByDropdown>
                  <!--                  <button class="btn btn-sm btn-soft-secondary" @click="showFilters = !showFilters"><i class="fas fa-sliders-h"></i> Filters</button>-->
                </div>
              </div>
              <!-- End: Search Results Header -->
              <!-- Start: Search Results -->
              <div class="row bbg-light">
                <div v-for="item in products" :key="item.uid" class="mb-5 col-md-12 text-center">
                  <ProductCard :item="item" :hide-monthly-cost="usingDefaultQuery" :monthly-comparison="searchQuery.currentMonthly" :query="searchQuery"/>
                </div>
              </div>
              <div v-if="moreSearchResultsAvailable" align="center">
                <button v-if="!isLoadingMore" @click="loadMore()" class="btn btn-soft-primary btn-block">Load More...</button>
                <button v-if="isLoadingMore" class="btn btn-soft-primary btn-block" type="button" disabled>
                  <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Loading...
                </button>
              </div>
              <!-- End: Search Results -->
            </div>
          </div>
        </div>
        <ProductQuickSearchModal :show="searchFormOpen" :query="currentSearchQuery" @saved="searchFormUpdated2($event, false)" @closed="searchFormOpen = false"/>
      </section>
    </div>
</template>

<script>
  import ProductNoProductsAvailable from "@/components/products/ProductNoProductsAvailable";
  import ProductCardPlaceholder from "@/components/products/ProductCardPlaceholder";
  import ProductQuickSearchModal from "@/components/products/ProductQuickSearchModal";
  import ProductCard from "@/components/products/ProductCard";
  import SortByDropdown from "@/components/search/SortByDropdown";
  import ProductFilters from "@/components/products/ProductFilters";
  import formDefaults from "@/config/searchFormDefaults.json";
  import {ProductSearchQuery} from "@/utils/ProductSearchQuery";
  import {ProductsApi} from "@/services/ProductsApi.ts";
  import ProductQuerySummary from "@/components/products/ProductQuerySummary";
  import BestBuyCard from "@/components/products/BestBuyCard";
  
  export default {
    name: "ProductExplorer",
    
    components: {
      BestBuyCard,
      ProductNoProductsAvailable,
      ProductCardPlaceholder,
      ProductQuickSearchModal,
      ProductCard,
      SortByDropdown,
      ProductFilters
    },

    data() {
      return {
        isSearching: false,
        isLoadingMore: false,
        searchQuery: {},
        searchFilters: formDefaults.filters,
        currentQueryType: null,
        usingDefaultQuery: true,
        searchFormOpen: false,
        sortByValue: "monthlyCost ASC",
        sortByQuery: {"field": "initialInterestRate", "order": 1},
        options: {
          rows: 5,
          start: 0,
        },
        totalResults: 0,
        products: [],
        facets: {},
        bestBuys: {},
        showFilters: false,
        showBestBuys: true
      }
    },

    props: {
        query: { 
          type: Object, 
          default: null 
        },
        filters: { 
          type: Object, 
          default: null 
        },
        disableSearchModal: {
          type: Boolean,
          default: false
        },
        debug: { 
          type: Boolean, 
          default: false 
        },
      onlyBestBuys: {
          type: Boolean,
          default: false
      },
      isExample: {
          type: Boolean,
          default: false
      },
      hideFilters: {
          type: Boolean,
          default: false
      }
    },

    mounted() {
      this.searchFormUpdated(this.query, this.usingDefaultQuery);
    },
    
    watch: {
      query: {
        deep: true,
        handler: function (val, oldVal) {
          if (JSON.stringify(val) === JSON.stringify(oldVal))
            return;
          
          this.searchFormUpdated(this.query, this.usingDefaultQuery);
        }
      }
    },
    
    methods: {
      refineFilters() {
        this.showFilters = true;
      },
      
      retrySearch() {
        if (this.disableSearchModal)
          this.$emit('retry');
        else
          this.searchFormOpen = true;
      },
      
      applyFilters(newFilters) {
        this.showFilters = false;
        this.searchFilters = { ...newFilters };
        this.executeSearch(0);
      },

      sortBySelected(event) {
        this.sortByValue = event.value;
        this.sortByQuery = event.sortBy;

        this.executeSearch(0);
      },

      loadMore() {
        this.executeSearch(this.products.length);
      },

      searchFormUpdated(queryData, usingDefault) {
        console.log("1. usingDefault = " + usingDefault);
        this.currentQueryType = queryData.type;
        this.usingDefaultQuery = usingDefault;
        this.searchQuery = new ProductSearchQuery(queryData);
        this.searchFilters = formDefaults.filters;
        this.executeSearch();
      },

      executeSearch(start) {
        this.isSearching = start === undefined || start === null || start === 0;
        this.isLoadingMore = !(start === null || start === undefined);
        let client = new ProductsApi(process.env.VUE_APP_PRODUCTS_API_URL);
        client.search(this.searchQuery.type, this.searchQuery.borrowingAmount(), 
                      this.searchQuery.propertyValue, this.searchQuery.term, 
                      start, this.options.rows, this.sortByValue, this.searchFilters,
              { "currentMonthly": this.searchQuery.currentMonthly })
            .then(products => {
              // console.log("Search returned: " + JSON.stringify(products));
              if (start !== undefined && start !== 0) {
                this.products = this.products.concat(products.results);
              } else {
                this.facets = products.facets;
                this.products = products.results;
                this.bestBuys = products.bestBuys;
                this.showBestBuys = true;
                this.$emit("data-available", products.results);
              }
              this.totalResults = products.totalResults;
              this.isSearching = false;
              this.isLoadingMore = false;
            })
            .catch(error => {
              this.isSearching = false;
              this.isLoadingMore = false;
              this.products = null;
            });
      },
    },

    computed: {
      filtersAreModified() {
        return JSON.stringify(formDefaults.filters) !== JSON.stringify(this.searchFilters);
      },

      currentSearchQuery: {
        get() {
          return this.query ?? null;
        },
        set(value) {
          this.query = value;
        }
      },

      isFailedSearch() {
        return this.products == null;
      },

      noSearchResultsAvailable() {
        return this.products != null && this.products.length === 0;
      },

      searchResultsAvailable() {
        return this.products != null && this.products.length > 0;
      },

      moreSearchResultsAvailable() {
        return this.products.length < this.totalResults;
      }
    }
  }
</script>

<style scoped>
  .flex-container {
    display: flex;
    flex-direction: row;
  }
  
  .flex-item-left {
    padding: 10px;
    flex: 50%;
  }
  
  .flex-item-right {
    padding: 10px;
    flex: 50%;
  }
  
  /* Responsive layout - makes a one column-layout instead of two-column layout */
  @media (max-width: 800px) {
    .flex-container {
      flex-direction: column;
    }
  }
</style>